import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createCheckoutSession, type Plan } from '../lib/api';

export default function Landing() {
  // `loadingPlan` doubles as "is anything spinning" + "which button to spin".
  // null = idle. Lets multiple Buy buttons coexist without each tracking
  // their own boolean and accidentally letting the user double-submit.
  const [loadingPlan, setLoadingPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleBuy(plan: Plan) {
    setError(null);
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckoutSession(plan);
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
      setLoadingPlan(null);
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 md:pt-32 md:pb-24">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
            Win your fantasy football draft with{' '}
            <span className="text-amber-400">AI on the clock</span>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed">
            DraftLion connects your live Sleeper draft to AI. When you're on the clock,
            ask for a pick — get ranked recommendations based on your current roster, value
            over replacement, positional scarcity, and other data. No spreadsheets, no missed picks.
          </p>
          <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed">
            DraftLion also evaluates trades. Tell your AI who's going each way and DraftLion
            scores the value for both sides so you can make an informed decision. Works for
            players and draft picks.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            {/* Hero CTA goes straight to the yearly plan because that's our
                highlighted recommendation. Secondary link below + the
                pricing section (#pricing) let monthly-seekers get there
                in one click. */}
            <button
              onClick={() => handleBuy('yearly')}
              disabled={loadingPlan !== null}
              className="btn-primary text-base"
            >
              {loadingPlan === 'yearly' ? 'Redirecting…' : 'Start 3-day free trial — yearly'}
            </button>
            <a href="#pricing" className="text-amber-400 hover:text-amber-300 text-sm font-medium">
              or compare plans →
            </a>
            <Link to="/setup" className="text-slate-300 hover:text-white text-sm">
              See how it works →
            </Link>
          </div>
          {error && <p className="mt-4 text-sm text-red-400">Something went wrong: {error}</p>}
          <p className="mt-4 text-xs text-slate-500">
            Then $22.99/year (or $3.99/month). Cancel anytime — no charge if you cancel during the trial.
          </p>
        </div>
      </section>

      {/* Demo video */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        {/* `overflow-hidden` clips the video to the container's rounded corners.
            `bg-black` shows behind any unavoidable letterboxing (object-cover
            should prevent it, but belt-and-suspenders). */}
        <div className="aspect-video rounded-xl overflow-hidden border border-slate-800 bg-black">
          <video
            className="w-full h-full object-cover"
            poster="https://assets-939204158944-us-east-1-an.s3.us-east-1.amazonaws.com/The+Draft+Lion+Demo-Cover.jpg"
            controls
            preload="metadata"
            playsInline
            title="DraftLion demo"
            src="https://assets-939204158944-us-east-1-an.s3.us-east-1.amazonaws.com/The+Draft+Lion+Demo.mp4"
          >
            Your browser does not support HTML5 video.
          </video>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="text-2xl md:text-3xl font-bold mb-12">What you get</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Feature
            title="Real-time draft state"
            body="Pulls picks from your live Sleeper draft as they happen. No copy-paste, no manual entry."
          />
          <Feature
            title="Roster-aware scoring"
            body="Knows what positions you still need. Won't recommend a fourth WR when you don't have a TE."
          />
          <Feature
            title="VORP-style value"
            body="Ranks players against the next-best available at the position. Catches steals and avoids reaches."
          />
          <Feature
            title="Injury & trend aware"
            body="Penalizes IR'd and questionable players. Surfaces trending adds from across the platform."
          />
          <Feature
            title="K / DEF discipline"
            body="Won't suggest a kicker in round 6. Pushes them to the rounds where they belong."
          />
          <Feature
            title="Works with your league's settings"
            body="Reads your league's roster slots and scoring systems. Recommendations match the format you actually play."
          />
          <Feature
            title="Trade Evaluation"
            body="Gives guidance on trades based on overall net value while factoring in positional need."
          />
          <Feature
            title="League Agnostic"
            body="Works for Redraft or Dynasty snake draft leagues with auction draft support coming soon."
          />
          <Feature
            title="AI Agnostic"
            body="Works with any AI agent that supports MCP. We provide setup instructions for Claude and ChatGPT."
          />
        </div>
      </section>

      {/* Pricing — 2 tiers, yearly is the highlighted recommendation.
          `id="pricing"` is the target of the "compare plans" hero link
          and the "choose monthly" link in the final CTA.

          `scroll-mt-32` (128px) reserves room for the sticky nav, which
          measures 103px tall. It was scroll-mt-20 (80px), which left the
          "Simple pricing" heading tucked ~23px underneath the nav after
          the jump. */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 pb-24 scroll-mt-32">
        <h2 className="text-2xl md:text-3xl font-bold mb-3 text-center">Simple pricing</h2>
        <p className="text-center text-slate-400 mb-12">
          Try free for 3 days. Cancel anytime.
        </p>

        {/* `items-stretch` on the grid keeps both cards the same height
            even though the yearly card has a "Best value" badge. */}
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Monthly — neutral styling, smaller emphasis */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 flex flex-col">
            <div className="text-sm font-semibold text-slate-400 uppercase tracking-wide">Monthly</div>
            <div className="mt-3 text-5xl font-extrabold">
              $3<span className="text-2xl text-slate-400">.99</span>
              <span className="text-base text-slate-400 font-normal">/month</span>
            </div>
            <p className="text-slate-400 mt-2 text-sm">
              3-day free trial. Then $3.99 per month. Cancel anytime.
            </p>
            <ul className="mt-6 space-y-3 text-slate-300 text-sm flex-1">
              <Bullet>Unlimited drafts across every Sleeper league</Bullet>
              <Bullet>Trade evaluations all season</Bullet>
              <Bullet>Email support</Bullet>
            </ul>
            <div className="mt-8">
              <button
                onClick={() => handleBuy('monthly')}
                disabled={loadingPlan !== null}
                className="w-full rounded-md px-5 py-3 font-semibold border border-slate-600 text-slate-200 hover:bg-slate-800 transition disabled:opacity-50"
              >
                {loadingPlan === 'monthly' ? 'Redirecting…' : 'Start free trial — monthly'}
              </button>
            </div>
          </div>

          {/* Yearly — highlighted. Amber border + ring + "Best value" tag.
              `relative` so we can absolute-position the badge in the corner. */}
          <div className="relative rounded-xl border-2 border-amber-500 ring-1 ring-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-8 flex flex-col shadow-lg shadow-amber-500/10">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-900 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
              Best value · Save 52%
            </div>
            <div className="text-sm font-semibold text-amber-400 uppercase tracking-wide">Yearly</div>
            <div className="mt-3 text-5xl font-extrabold">
              $22<span className="text-2xl text-slate-400">.99</span>
              <span className="text-base text-slate-400 font-normal">/year</span>
            </div>
            <p className="text-slate-400 mt-2 text-sm">
              3-day free trial. Then $22.99 per year — works out to ~$1.92/month.
            </p>
            <ul className="mt-6 space-y-3 text-slate-300 text-sm flex-1">
              <Bullet>Everything in monthly</Bullet>
              <Bullet><strong>Save 52%</strong> vs paying month-to-month</Bullet>
              <Bullet>Locked-in pricing for a full season</Bullet>
            </ul>
            <div className="mt-8">
              <button
                onClick={() => handleBuy('yearly')}
                disabled={loadingPlan !== null}
                className="btn-primary w-full text-base"
              >
                {loadingPlan === 'yearly' ? 'Redirecting…' : 'Start free trial — yearly'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <p className="mt-6 text-center text-sm text-red-400">
            Something went wrong: {error}
          </p>
        )}

        <p className="mt-8 text-center text-xs text-slate-500">
          We collect a card to start your trial. You won't be charged until day 4.
          Cancel from the Manage page anytime before then with one click.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">FAQs</h2>
        <div className="space-y-8">
          <Faq q="Is the free trial really free?">
            Yes. We collect a card on file via Stripe Checkout so the trial converts
            seamlessly to a paid subscription if you decide to keep it. If you cancel
            before day 4 you're never charged a cent. Free trials are one per customer —
            if you've subscribed to DraftLion before, your subscription starts and your
            card is charged right away.
          </Faq>
          <Faq q="When am I charged?">
            On day 4 of your trial, at the plan you signed up for ($3.99 for monthly
            or $22.99 for yearly). We'll email you before the charge so it's never
            a surprise. If you've had a DraftLion subscription before, you're charged
            at signup instead, since trials are one per customer.
          </Faq>
          <Faq q="Can I switch between monthly and yearly later?">
            Yes. From the <Link to="/manage" className="text-amber-400 hover:text-amber-300">Manage page</Link>{' '}
            you can switch plans anytime. Stripe prorates the difference automatically —
            so upgrading mid-month gives you credit, and the yearly plan kicks in immediately.
          </Faq>
          <Faq q="How does it work with my Sleeper draft?">
            DraftLion uses Sleeper's public API. You give your AI agent your draft ID
            (one number from your Sleeper URL) and it does the rest — pulling picks,
            checking your roster, ranking the next-best players for you.
          </Faq>
          <Faq q="Will it slow down during my actual draft?">
            No. The server runs on AWS Lambda with a shared cache, so even when 12 people
            in your league are all asking your AI agent at the same time, every recommendation
            will run as quick as your AI agent normally runs.
          </Faq>
          <Faq q="Does it work with my league's scoring (PPR / Half-PPR / Standard)?">
            Yes. It reads your league settings live — roster slots, scoring,
            number of teams — and tailors recommendations accordingly.
          </Faq>
          <Faq q="What happens after my draft is over?">
            Your subscription stays active until the end of your billing period (a month
            or a year, depending on plan). You can use it for additional drafts (mock
            drafts, second leagues, etc.) and for trade recommendations to continually
            improve your roster.
          </Faq>
          <Faq q="Can I cancel?">
            Yes. <Link to="/manage" className="text-amber-400 hover:text-amber-300">Manage your subscription here</Link> —
            cancellation is quick and easy. If you cancel <strong>during the trial</strong>, you
            won't be charged and your access ends right away. If you cancel{' '}
            <strong>after a paid period starts</strong>, your access continues through the end of
            the period you've paid for.
          </Faq>
          <Faq q="What happens if I cancel during the free trial?">
            You won't be charged anything, and your DraftLion access ends as soon as you cancel.
            The trial is there to let you try the real thing before you pay — so if it's working
            for you, just leave it running and it converts automatically on day 4.
          </Faq>
          <Faq q="What's the refund policy?">
            We do not offer refunds at this time. The 3-day free trial exists so you can
            decide whether DraftLion is worth it before any money changes hands.
          </Faq>
          <Faq q="Which AI agents does it work with?">
            DraftLion is built for any AI agents that support custom MCPs. The most common ones are Claude and
            Chat GPT. We have setup instructions for the desktop versions for both of these (Mac and Windows). If you
            use a different AI tool, email support@thedraftlion.com and we can walk you through how to set it up.
          </Faq>
          <Faq q="What if I hit my AI tool's usage limits during my draft?">
            If you run into usage limits, recommendations will pause until your limit resets.
            We strongly recommend <strong>Claude Pro or ChatGPT Plus</strong>{' '}
            during draft season — and even then, it's worth doing a 5-minute test run an hour
            before your draft to confirm everything works.
          </Faq>
          <Faq q="Can I use it for leagues not on Sleeper?">
            We have plans to add support for Yahoo fantasy leagues. ESPN is also something we hope to
            support, but ESPN does not have public APIs we can read data from in real time to give
            recommendations.
          </Faq>
          <Faq q="Can you guarantee I win my fantasy league if using this?">
            Honestly, no. We integrate with AI and give recommendations based on various data points.
            We think it will give you an advantage, but we cannot promise you will be the champion.
          </Faq>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-32 text-center">
        <h2 className="text-2xl md:text-3xl font-bold">Draft season is short.</h2>
        <p className="mt-4 text-slate-300">Try free for 3 days. Cancel anytime.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <button
            onClick={() => handleBuy('yearly')}
            disabled={loadingPlan !== null}
            className="btn-primary text-base"
          >
            {loadingPlan === 'yearly' ? 'Redirecting…' : 'Start 3-day free trial — yearly'}
          </button>
          <a href="#pricing" className="text-amber-400 hover:text-amber-300 text-sm font-medium">
            or choose monthly →
          </a>
        </div>
      </section>
    </>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
      <h3 className="font-semibold text-white">{title}</h3>
      <p className="mt-2 text-slate-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-amber-400 mt-0.5" aria-hidden>✓</span>
      <span>{children}</span>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-semibold text-white">{q}</h3>
      <p className="mt-2 text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}
